import { useQuery } from "@tanstack/react-query";

import { api } from "../api/rest";
import type {
  MispricedGridResponse,
  MispricedGroup,
  MispricedSignal,
  MispricedSlice,
} from "../api/types";

/**
 * Такт опроса СВОДКИ. Её читают глазами, и секунда задержки ничего не стоит.
 */
const MISPRICED_REFETCH_MS = 15_000;

/**
 * Такт опроса ЛЕНТЫ — втрое чаще сводки, и это не запас.
 *
 * С приходом порта постановки (ADR-0022) по строке ленты стоит кнопка ступени
 * `alert`, а живёт эта кнопка ровно до конца окна: строка рождается за секунды
 * до него. На такте в пятнадцать секунд оператор увидел бы карточку уже
 * погасшей. То же число и тот же довод, что у почти решённого и у семей.
 */
const MISPRICED_FEED_REFETCH_MS = 3_000;

/** Сводка расхождения по парам «источник × роль». */
export function useMispricedSummary(hours: number, windowLen = 0) {
  return useQuery<{ hours: number; groups: MispricedGroup[] }>({
    queryKey: ["mispriced-summary", hours, windowLen],
    queryFn: () => api.mispricedSummary(hours, windowLen),
    refetchInterval: MISPRICED_REFETCH_MS,
    retry: 1,
  });
}

/**
 * Разрез счёта по минуте захода и цене плана — на все пары одним запросом.
 *
 * Хук монтируется только под РАЗВЁРНУТОЙ строкой сводки, поэтому свёрнутая
 * панель разреза не опрашивает вовсе — тот же приём, что у тел FoldablePanel.
 * Две развёрнутые строки складываются в один запрос ключом TanStack.
 */
export function useMispricedBreakdown(hours: number, windowLen = 0) {
  return useQuery<{ hours: number; slices: MispricedSlice[] }>({
    queryKey: ["mispriced-breakdown", hours, windowLen],
    queryFn: () => api.mispricedBreakdown(hours, windowLen),
    refetchInterval: MISPRICED_REFETCH_MS,
    retry: 1,
  });
}

/**
 * Сетка порога кромки: что дала бы каждая ступень MISPRICED_TAKER_MARGIN.
 *
 * Хук монтируется только под РАЗВЁРНУТОЙ панелью сетки, поэтому свёрнутая её не
 * опрашивает вовсе — тот же приём и тот же довод, что у тел FoldablePanel и у
 * разреза.
 *
 * Такт — как у сводки, и это не про нагрузку. Сетка отвечает на вопрос о
 * КАЛИБРОВКЕ («не мал ли порог»), а он меняется сутками: чаще опрашивать нечего.
 */
export function useMispricedGrid(hours: number, windowLen = 0) {
  return useQuery<MispricedGridResponse>({
    queryKey: ["mispriced-grid", hours, windowLen],
    queryFn: () => api.mispricedGrid(hours, windowLen),
    refetchInterval: MISPRICED_REFETCH_MS,
    retry: 1,
  });
}

/** Лента теневых заявок, свежие сверху. */
export function useMispricedSignals(hours: number, limit = 60, windowLen = 0) {
  return useQuery<{ signals: MispricedSignal[] }>({
    queryKey: ["mispriced-signals", hours, limit, windowLen],
    queryFn: () => api.mispricedSignals(hours, undefined, undefined, limit, windowLen),
    refetchInterval: MISPRICED_FEED_REFETCH_MS,
    // В фоновой вкладке опрос не идёт: карточка нужна тому, кто смотрит.
    refetchIntervalInBackground: false,
    retry: 1,
  });
}
